import axios, { isAxiosError } from "axios";
import * as moment from "moment";

import { FileAnswer } from "@teamprevent/common/questionnaires/model";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";
import { ImmunisationClearCertificateModel, parseImmunisationClearCertificateModel } from "@teamprevent/common/immunisation/models";
import { ServerModelWithDateStrings } from "@teamprevent/common/utils";

import { parseTaskModel } from "../task/model";
import { FollowUpTaskModel } from "../immunisationReview/model";
import { EppDecision, Covid19VaccinationStatus } from "../immunisations/model";

class ImmunisationOhCaseApi {
    public async clear(
        customerId: string,
        employeeId: string,
        ohCaseId: string,
        eppDecision: EppDecision,
        internalNotes: string,
        notes: string,
        covid19VaccinationStatus: Covid19VaccinationStatus,
        bookedDateOfFirstVaccination: moment.Moment,
        dateOfFirstVaccination: moment.Moment,
        dateOfSecondVaccination: moment.Moment): Promise<void> {
        try {
            await axios.post(
                `/api/customer/${customerId}/employeeRecord/${employeeId}/immunisationCase/${ohCaseId}/clear`,
                {
                    eppDecision,
                    internalNotes,
                    notes,
                    covid19VaccinationStatus,
                    bookedDateOfFirstVaccination,
                    dateOfFirstVaccination,
                    dateOfSecondVaccination
                });
        } catch (axiosError) {
            if (isAxiosError(axiosError) && axiosError.response?.status === 400) {
                throw new Error(axiosError.response.data);
            }

            throw axiosError;
        }
    }

    public async addBloodTestOutcome(
        customerId: string,
        employeeId: string,
        ohCaseId: string,
        taskId: string,
        outcome: string,
        details: string,
        followUpTasks: FollowUpTaskModel[]): Promise<TaskModel[]> {
        const parsedTasksModel = followUpTasks.map<ServerModelWithDateStrings<FollowUpTaskModel>>(m => {
            return {
                ...m,
                dueDate: m.dueDate?.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS)
            };
        });

        const response = await axios.post(
            `/api/customer/${customerId}/employeeRecord/${employeeId}/immunisationCase/${ohCaseId}/bloodtestoutcome/${taskId}`,
            { outcome, details, followUpTasks: parsedTasksModel });
        return response.data.map(parseTaskModel);
    }

    public async addBloodTestResult(
        customerId: string,
        employeeId: string,
        ohCaseId: string,
        taskId: string,
        result: string,
        evidence: FileAnswer): Promise<TaskModel[]> {
        const response = await axios.post(
            `/api/customer/${customerId}/employeeRecord/${employeeId}/immunisationCase/${ohCaseId}/bloodtestresult/${taskId}`,
            { result, evidence });
        return response.data.map(parseTaskModel);
    }

    public async getImmunisationClearCertificateDetails(customerId: string, employeeId: string, ohCaseId: string, taskId: string) {
        const response = await axios.get<ImmunisationClearCertificateModel>(
            `/api/customer/${customerId}/employeeRecord/${employeeId}/immunisationCase/${ohCaseId}/immunisationClearCertificate/${taskId}`
        );
        return parseImmunisationClearCertificateModel(response.data);
    }
}

export const immunisationOhCaseApi = new ImmunisationOhCaseApi();
